import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Trophy, TrendingUp, TrendingDown, Plane, Coins } from "lucide-react";

const TABS = [
  { id: "purchases",    label: "Acquisti Top",     icon: Coins,        color: "amber" },
  { id: "plusvalenze",  label: "Plusvalenze",      icon: TrendingUp,   color: "emerald" },
  { id: "minusvalenze", label: "Minusvalenze",     icon: TrendingDown, color: "rose" },
  { id: "traveled",     label: "Più Movimentati",  icon: Plane,        color: "sky" },
];

const COLOR_CLS = {
  amber:   { border: "border-amber-500/40",   bg: "bg-amber-500/10",   text: "text-amber-300",   accent: "text-amber-400" },
  emerald: { border: "border-emerald-500/40", bg: "bg-emerald-500/10", text: "text-emerald-300", accent: "text-emerald-400" },
  rose:    { border: "border-rose-500/40",    bg: "bg-rose-500/10",    text: "text-rose-300",    accent: "text-rose-400" },
  sky:     { border: "border-sky-500/40",     bg: "bg-sky-500/10",     text: "text-sky-300",     accent: "text-sky-400" },
};

export default function RankingsMovimenti() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("purchases");
  const [clubFilter, setClubFilter] = useState("ALL");
  const [seasonFilter, setSeasonFilter] = useState("ALL");

  useEffect(() => {
    api.get("/magnati/rankings")
      .then(r => setData(r.data))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-screen bg-slate-950 text-slate-400 flex items-center justify-center text-sm">Caricamento classifiche…</div>;
  if (!data) return null;

  // Estraggo la lista di club unici da tutte le classifiche per popolare il select
  const clubsSet = new Map();
  (data.top_purchases || []).forEach(p => { if (p.club_id) clubsSet.set(p.club_id, p.club_name); });
  (data.top_plusvalenze || []).concat(data.top_minusvalenze || []).forEach(t => {
    if (t.club_id) clubsSet.set(t.club_id, t.club_name);
    if (t.next_club_id) clubsSet.set(t.next_club_id, t.next_club_name);
  });
  const clubs = Array.from(clubsSet.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  const selectedName = clubFilter === "ALL" ? null : (clubsSet.get(clubFilter) || null);

  // Estraggo la lista di stagioni uniche
  const seasonSet = new Set();
  (data.top_purchases || []).forEach(p => { if (p.buy_season) seasonSet.add(p.buy_season); });
  (data.top_plusvalenze || []).concat(data.top_minusvalenze || []).forEach(t => {
    if (t.buy_season) seasonSet.add(t.buy_season);
    if (t.sell_season) seasonSet.add(t.sell_season);
  });
  const seasons = Array.from(seasonSet).sort();
  const seasonApplicable = tab !== "traveled";  // il tab "movimentati" è aggregato, filtro stagione non ha senso

  const rawItems = data[tabToKey(tab)] || [];
  const filteredItems = rawItems.filter(it => {
    // Club filter
    if (clubFilter !== "ALL") {
      if (tab === "purchases" && it.club_id !== clubFilter) return false;
      if ((tab === "plusvalenze" || tab === "minusvalenze") && it.club_id !== clubFilter) return false;
      if (tab === "traveled" && !(it.clubs || []).includes(selectedName)) return false;
    }
    // Season filter (solo dove ha senso)
    if (seasonApplicable && seasonFilter !== "ALL") {
      if (tab === "purchases" && it.buy_season !== seasonFilter) return false;
      if ((tab === "plusvalenze" || tab === "minusvalenze") && it.sell_season !== seasonFilter) return false;
    }
    return true;
  });
  // Se "Tutti", mostro solo top 50 per non rendere una lista chilometrica
  const noFilters = clubFilter === "ALL" && (seasonFilter === "ALL" || !seasonApplicable);
  const items = noFilters ? filteredItems.slice(0, 50) : filteredItems;

  const cur = TABS.find(t => t.id === tab);
  const cls = COLOR_CLS[cur.color];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="bg-slate-900/60 border-b border-slate-800 sticky top-0 z-10 backdrop-blur">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="../sinottica" className="p-1.5 rounded-md text-slate-500 hover:text-white hover:bg-slate-800" data-testid="rankings-back">
            <ArrowLeft size={16}/>
          </Link>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Storia Lega · Colpi da Manuale</div>
            <h1 className="text-2xl font-black text-white flex items-center gap-2"><Trophy size={22} className="text-amber-400"/> Classifica Movimenti</h1>
          </div>
          <div className="ml-auto flex items-center gap-4 text-xs text-slate-500 font-mono">
            <div><b className="text-slate-200">{data.counts.total_purchases}</b> acquisti</div>
            <div><b className="text-slate-200">{data.counts.total_closed_trades}</b> operazioni chiuse</div>
            <div><b className="text-slate-200">{data.counts.total_traveled_players}</b> giocatori girovaghi</div>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 py-6 space-y-6">
        {/* Tab bar */}
        <div className="flex flex-wrap gap-2 border-b border-slate-800 pb-2">
          {TABS.map(t => {
            const active = tab === t.id;
            const c = COLOR_CLS[t.color];
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`rankings-tab-${t.id}`}
                className={`px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-all inline-flex items-center gap-2 ${
                  active
                    ? `${c.bg} ${c.border} border ${c.text}`
                    : "text-slate-500 hover:text-slate-200 border border-transparent"
                }`}
              >
                <Icon size={14}/> {t.label}
                <span className={`text-[9px] font-mono opacity-60 ${active ? c.text : ""}`}>{(data[tabToKey(t.id)] || []).length}</span>
              </button>
            );
          })}
        </div>

        {/* Filters: club + season */}
        <div className="flex flex-wrap items-center gap-3" data-testid="rankings-filters">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Club</span>
            <select
              value={clubFilter}
              onChange={e => setClubFilter(e.target.value)}
              data-testid="rankings-club-select"
              className="bg-slate-950 border border-slate-700 rounded-md px-3 py-1.5 text-sm text-slate-100 focus:border-amber-500 focus:outline-none min-w-[200px]"
            >
              <option value="ALL">Tutti i club</option>
              {clubs.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] font-mono uppercase tracking-widest ${seasonApplicable ? "text-slate-500" : "text-slate-700"}`}>
              Stagione {!seasonApplicable && "(n/d)"}
            </span>
            <select
              value={seasonFilter}
              onChange={e => setSeasonFilter(e.target.value)}
              disabled={!seasonApplicable}
              data-testid="rankings-season-select"
              className="bg-slate-950 border border-slate-700 rounded-md px-3 py-1.5 text-sm text-slate-100 focus:border-amber-500 focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <option value="ALL">Tutte le stagioni</option>
              {seasons.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          {(clubFilter !== "ALL" || (seasonApplicable && seasonFilter !== "ALL")) && (
            <>
              <span className={`text-xs font-mono ${cls.text}`}>
                {filteredItems.length} risultati
                {clubFilter !== "ALL" && <> · <b>{selectedName}</b></>}
                {seasonApplicable && seasonFilter !== "ALL" && <> · <b>{seasonFilter}</b></>}
              </span>
              <button
                onClick={() => { setClubFilter("ALL"); setSeasonFilter("ALL"); }}
                data-testid="rankings-filters-clear"
                className="text-[10px] font-mono uppercase tracking-widest text-slate-500 hover:text-white underline ml-auto"
              >
                Reset filtri
              </button>
            </>
          )}
          {noFilters && (
            <span className="text-[10px] font-mono text-slate-600 ml-auto">Mostro top 50 su {rawItems.length}</span>
          )}
        </div>

        {/* Content */}
        {items.length === 0 ? (
          <div className="text-center text-slate-500 py-12 text-sm">Nessun dato in questa classifica</div>
        ) : (
          <div className="space-y-2" data-testid={`rankings-list-${tab}`}>
            {items.map((item, i) => (
              <RankingRow key={i} idx={i} item={item} tab={tab} cls={cls}/>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function tabToKey(tabId) {
  if (tabId === "purchases")    return "top_purchases";
  if (tabId === "plusvalenze")  return "top_plusvalenze";
  if (tabId === "minusvalenze") return "top_minusvalenze";
  if (tabId === "traveled")     return "most_traveled";
  return "top_purchases";
}

function RankingRow({ idx, item, tab, cls }) {
  const rank = idx + 1;
  const medal = rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : `#${rank}`;

  if (tab === "purchases") {
    return (
      <div className={`p-3 rounded-xl border ${cls.border} ${cls.bg} flex items-center gap-4`}>
        <div className="w-10 text-lg font-black text-center shrink-0">{medal}</div>
        <div className="flex-1 min-w-0">
          <Link to={`/storico-magnati/giocatore/${item.player_id}`} className="font-bold text-white text-base hover:underline truncate block">{item.player_name}</Link>
          <div className="text-xs text-slate-400">{item.club_name} · {item.buy_season}</div>
        </div>
        <div className="text-right shrink-0">
          <div className={`text-2xl font-black font-mono ${cls.text}`}>{item.buy_cost.toFixed(0)}</div>
          <div className={`text-[10px] font-mono uppercase tracking-widest ${cls.accent}`}>crediti</div>
        </div>
      </div>
    );
  }

  if (tab === "plusvalenze" || tab === "minusvalenze") {
    const isPos = item.profit > 0;
    return (
      <div className={`p-3 rounded-xl border ${cls.border} ${cls.bg} flex items-center gap-4`}>
        <div className="w-10 text-lg font-black text-center shrink-0">{medal}</div>
        <div className="flex-1 min-w-0">
          <Link to={`/storico-magnati/giocatore/${item.player_id}`} className="font-bold text-white text-base hover:underline truncate block">{item.player_name}</Link>
          <div className="text-xs text-slate-400 truncate">
            {item.club_name} <span className="text-slate-600 mx-1">→</span> {item.next_club_name}
            <span className="text-slate-600 mx-1">·</span>
            {item.buy_season} → {item.sell_season}
            <span className="text-slate-600 mx-1">·</span>
            {item.hold_seasons} stag.
          </div>
        </div>
        <div className="text-right shrink-0 flex items-center gap-3">
          <div className="text-xs font-mono text-slate-500">
            <span className="text-slate-300">{item.buy_cost}</span>
            <span className="mx-1">→</span>
            <span className="text-slate-300">{item.sell_cost}</span>
          </div>
          <div>
            <div className={`text-2xl font-black font-mono ${cls.text}`}>{isPos ? "+" : ""}{item.profit.toFixed(0)}</div>
            {item.roi_pct != null && <div className={`text-[10px] font-mono uppercase tracking-widest ${cls.accent}`}>{isPos ? "+" : ""}{item.roi_pct}%</div>}
          </div>
        </div>
      </div>
    );
  }

  // traveled
  return (
    <div className={`p-3 rounded-xl border ${cls.border} ${cls.bg} flex items-center gap-4`}>
      <div className="w-10 text-lg font-black text-center shrink-0">{medal}</div>
      <div className="flex-1 min-w-0">
        <Link to={`/storico-magnati/giocatore/${item.player_id}`} className="font-bold text-white text-base hover:underline truncate block">{item.player_name}</Link>
        <div className="text-[10px] text-slate-400 truncate mt-1">
          {item.clubs.join(" → ")}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className={`text-2xl font-black font-mono ${cls.text}`}>{item.moves}</div>
        <div className={`text-[10px] font-mono uppercase tracking-widest ${cls.accent}`}>cambi</div>
      </div>
    </div>
  );
}
